import { ref, watch } from 'vue';
import type { Router } from 'vue-router';
import { useRouter } from 'vue-router';
import type { ConvexClient } from 'convex/browser';
import { useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import { useSupabaseClient } from './useSupabaseClient';
import { useToast } from './useToast';

const OTHER_DEVICE_MESSAGE = 'Signed out — your account was signed in on another device.';
const SESSION_ENDED_MESSAGE = 'Your session has ended. Please sign in again.';

// One-shot: an explicit sign-in wants this device to claim the active session.
// Consumed by claimActiveSessionIfPending so the claim fires exactly once per
// login and never on session restore/refresh (which would make two live devices
// fight over the slot).
const claimRequested = ref(false);

// Suppresses the takeover sign-out while our own login is settling, so a fresh
// sign-in never kicks itself before its claim is reflected. Cleared once our
// session is confirmed active. Kept separate from claimRequested: on a first-ever
// login sessionStatus reads `active` before any claim exists, which would
// otherwise clear the request and skip the claim.
const claiming = ref(false);

// Router captured when the watcher mounts (App setup) so the module-level forced
// logout can navigate without a component instance in scope.
let appRouter: Router | null = null;

// Guards the forced-logout flow so a burst of failed token refreshes (or a
// reactive takeover firing alongside them) only signs out and redirects once.
let loggingOut = false;

export function markLoginIntent() {
  claimRequested.value = true;
  claiming.value = true;
  loggingOut = false; // a fresh login re-arms the forced-logout guard
}

export function clearLoginIntent() {
  claimRequested.value = false;
  claiming.value = false;
}

// Claim the active session for this device. Called from useConvexAuth after the
// user row is upserted; no-ops unless an explicit login requested it.
export async function claimActiveSessionIfPending(client: ConvexClient) {
  if (!claimRequested.value) return;
  claimRequested.value = false;
  try {
    await client.mutation(api.users.setActiveSession, {});
  } catch (err) {
    console.error('[Auth] Failed to claim active session:', err);
    claiming.value = false; // resume enforcement; the claim didn't land
  }
}

// Sign out and return to /auth when this device's session is superseded — either
// detected reactively (lost the active slot) or via a revoked/expired token
// refresh. Uses local scope so a kicked device never revokes the winning device.
async function forceLogout(message: string) {
  if (loggingOut) return;
  loggingOut = true;
  const { supabase } = useSupabaseClient();
  const { createToast } = useToast();
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch (err) {
    console.error('[Auth] Local sign-out after session takeover failed:', err);
  }
  createToast({ type: 'info', message, duration: 5000 });
  appRouter?.replace({ path: '/auth' });
}

// Called from useConvexAuth when a token refresh fails because the session was
// revoked or expired (e.g. signed in on a newer device).
export function handleSessionRevoked() {
  forceLogout(SESSION_ENDED_MESSAGE);
}

// Watches whether this device still owns the single active session; signs out
// the moment another device signs in.
export function useSingleDeviceSession() {
  const { data } = useConvexQuery(api.users.sessionStatus, {});
  appRouter = useRouter();

  watch(() => data.value?.active, (active) => {
    if (active === true) {
      claiming.value = false; // our session is confirmed active
      return;
    }
    if (active === false && !claiming.value) {
      forceLogout(OTHER_DEVICE_MESSAGE);
    }
  });
}
